import { Api, LogManager, ModelRow } from "@mcleod/core";
import { ClickEvent, Label, Snackbar, TableRowDisplayEvent } from "@mcleod/components";
import { FilterPanel, OrderTabView } from "@mcleod/dispatch";
import { AutogenLayoutInvoiceProcessingOnHold } from "./autogen/AutogenLayoutInvoiceProcessingOnHold";
import { Filter, InvoiceProcessingTableType } from "./InvoiceProcessingTableType";
import { InvoiceProcessingTableListeners, updateFilterCounts, InvoiceProcessingTable, scheduleRefreshWithDelay } from "./InvoiceProcessingUtil";
import { InvoiceProcessingToolPanel } from "./InvoiceProcessingToolPanel";
import { openManageStops } from "./InvoiceProcessing";

const log = LogManager.getLogger("lme.ar.InvoiceProcessingOnHold");

export class InvoiceProcessingOnHold extends AutogenLayoutInvoiceProcessingOnHold implements InvoiceProcessingTable {
    public filterPanel: FilterPanel;
    public type: InvoiceProcessingTableType;
    private _selectedMoveId: string = "";

    tableProcessingOnRowDisplay(event: TableRowDisplayEvent): void {
        const tableRow = event.getTableRow();
        const modelRow = event.getTableRow().data as ModelRow;
        tableRow.contextMenuItems = () => InvoiceProcessingToolPanel.getContextMenuItems(this.layoutInvoiceProcessingToolPanel, tableRow);
        InvoiceProcessingTableListeners.invoiceProcessingOnRowDisplayListener(tableRow, modelRow);
        InvoiceProcessingTableListeners.onRowDisplayOrderCount(tableRow, modelRow, this.tableProcessing);
        const labelOrderId = tableRow.findComponentById("labelOrderId") as Label;
        if (labelOrderId) {
            labelOrderId.addUnderlineWhenMouseOver();
        }
        tableRow.addDblClickListener(event => {
            this.selectedMoveId = modelRow.get("movement_id");
        });
    }

    configureTableForType(type: InvoiceProcessingTableType): void {
        this.type = type;
        this.tableProcessing.dataSource.orderBy = this.type.defaultSort;
        this.filterPanel = new FilterPanel(type.secondaryFilters, this.tableProcessing, this.panelSecondaryFilters);
        const originalFilters = this.filterPanel.filters;
        this.filterPanel.updateFilterCounts = () => updateFilterCounts(originalFilters, type, this.filterPanel, this.mainDataSource, true);
        this.layoutInvoiceProcessingToolPanel.addMountListener(() => {
            this.tableProcessing.addMountListener(() => this.layoutInvoiceProcessingToolPanel.initialize(this))
        });
    }

    public get filter(): Filter {
        return this.type.filter;
    }

    onClickOrderIdCount(event: ClickEvent) {
        if (event.target instanceof Label) {
            const orderId = event.target.boundRow.get("order_id")
            this.tableProcessing.setFilter(`order_id=${orderId}`);
        }
    }

    /** This is an event handler for the onClick event of labelOrderId. */
    labelOrderIdOnClick(event: ClickEvent) {
        const orderId = (event.target as Label).text.replace("[", "").replace("]", "");
        OrderTabView.navigateTo({ key: orderId, newTab: true });
    }

    /** This is an event handler for the afterCrudClose event of tableProcessing. */
    async tableProcessingAfterCrudClose(event) {
        const row =
        {
            billing_id:  event.target.layout.sourceBilling.data[0].get("id"),
            order_id: event.target.layout.sourceBilling.data[0].get("order_id"),
            movement_id:  ( event.target.layout.sourceOrders.data.length > 0 )?event.target.layout.sourceOrders.data[0].get("curr_movement_id"):this.selectedMoveId
        };
        const selected = [row];
        const holdChanged = event.target.layout.holdReasonChanged;
        const reasonCode = event.target.layout.sourceBilling.data[0].get("hold_reason_code");
        const reasonCodeText = event.target.layout.sourceBilling.data[0].get("hold_reason_text");

         if ((holdChanged) && (!reasonCode)) {
            const result = await Api.post("lme/ar/invoice-remove-hold", { billing_id_list: selected, save_billing_row: false });
             if (!result) {
                Snackbar.showWarningSnackbar("Error placing invoice on hold.");
             } else {
                this.refreshData();
             }

         }
      }
      public get selectedMoveId(): string {
        return this._selectedMoveId;
    }
        public set selectedMoveId(value: string) {
            this._selectedMoveId = value;
        }

    /** This is an event handler for the onClick event of labelStopsPickDrop. */
      labelStopsPickDropOnClick(event: ClickEvent) {
        openManageStops(event);
      }

      private refreshData(recordCount?: number) {
        scheduleRefreshWithDelay(() => this.doRefreshData(), recordCount, "lme.ar.InvoiceProcessingOnHold");
    }

    private doRefreshData() {
        log.debug("Starting data refresh after state change");

        if (!this.mainDataSource) {
            console.error("InvoiceProcessingOnHold: No mainDataSource available for refresh");
            log.error("InvoiceProcessingOnHold: No mainDataSource available for refresh");
            return;
        }

        // Try to find the main InvoiceProcessing layout first
        const mainLayout = this.findMainInvoiceProcessingLayout();
        if (mainLayout) {
            log.debug("Found InvoiceProcessing layout, using searchMainDatasource to preserve profiles");
            mainLayout.searchMainDatasource();
            setTimeout(() => this.updateAllFilterCounts(), 200);
            return;
        }

        // Fallback: Find profile from parent hierarchy
        const profileRow = this.findProfileFromParent();
        if (profileRow) {
            log.debug("Using profile from parent layout");
            this.executeDataRefresh({ profile_row: profileRow }, "profile from parent layout");
            return;
        }

        // Last resort: Generic refresh (will reset to default profile)
        log.debug("No profile found, performing generic refresh (will reset to default profile)");
        this.executeDataRefresh({}, "generic refresh (will reset to default profile)");
    }

    /**
     * Finds the main InvoiceProcessing layout by walking up the parent hierarchy
     */
    private findMainInvoiceProcessingLayout(): any {
        let layout = this as any;
        while (layout && layout.parent) {
            layout = layout.parent;
            if (layout.constructor.name === 'InvoiceProcessing' || layout.searchMainDatasource) {
                return layout;
            }
        }
        return null;
    }

    /**
     * Finds a profile row from the parent hierarchy
     */
    private findProfileFromParent(): any {
        let currentLayout = this as any;
        while (currentLayout && currentLayout.parent) {
            currentLayout = currentLayout.parent;
            if (currentLayout.profileRow || currentLayout._profileRow) {
                const profileRow = currentLayout.profileRow || currentLayout._profileRow;
                if (profileRow && profileRow.get && profileRow.get("id")) {
                    return profileRow;
                }
            }
        }
        return null;
    }

    /**
     * Executes the data refresh with the given search parameters
     */
    private executeDataRefresh(searchParams: any, refreshType: string) {
        this.mainDataSource.clear();
        this.mainDataSource.search(searchParams)
            .then(() => {
                log.debug(`${refreshType} completed, updating filter counts`);
                this.updateAllFilterCounts();
            })
            .catch(error => {
                console.error(`Error during ${refreshType}:`, error);
                log.error(`Error during ${refreshType}:`, error);
            });
    }

    private updateAllFilterCounts() {
        // Force update of all filter counts to ensure tabs reflect current data
        log.debug("Starting filter count update");

        setTimeout(() => {
            try {
                // Update filter counts for this tab
                if (this.filterPanel && this.filterPanel.updateFilterCounts) {
                    this.filterPanel.updateFilterCounts();
                }

                // Try to update filter counts for sibling tabs (less aggressive approach)
                this.updateSiblingTabCounts();                } catch (error) {
                log.error("Error updating filter counts:", error);
            }
        }, 200); // Increased from 100ms to 200ms for multi-record operations

        // Do a second, more conservative pass to catch any delayed updates
        setTimeout(() => {
            try {
                // Second pass - just update filter counts, data source refresh should handle the data
                if (this.filterPanel && this.filterPanel.updateFilterCounts) {
                    this.filterPanel.updateFilterCounts();
                }
                // Only update siblings, not aggressive refresh
                this.updateSiblingTabCounts();
            } catch (error) {
                log.error("Error in second pass filter count update:", error);
            }
        }, 800); // Increased from 500ms to 800ms for multi-record operations
      }

      private updateSiblingTabCounts() {
        try {
            let parentLayout = this as any;
            let foundParent = false;

            // Walk up to find the parent that contains our sibling tabs
            while (parentLayout && parentLayout.parent && !foundParent) {
                parentLayout = parentLayout.parent;

                // Look for sibling tab layouts (OnHold, ReadyToProcess, etc.)
                if (parentLayout.children) {
                    let invoiceProcessingTabCount = 0;
                    for (const child of parentLayout.children) {
                        if (child.constructor.name.includes('InvoiceProcessing')) {
                            invoiceProcessingTabCount++;
                        }
                    }

                    // If we found multiple invoice processing tabs, this is likely the right parent
                    if (invoiceProcessingTabCount > 1) {
                        foundParent = true;
                        log.debug(`Found parent with ${invoiceProcessingTabCount} invoice processing tabs`);

                        for (const child of parentLayout.children) {
                            if (child !== this && child.filterPanel && child.filterPanel.updateFilterCounts) {
                                child.filterPanel.updateFilterCounts();
                            }
                        }
                    }
                }
            }

            if (!foundParent) {
                log.debug("Could not find parent with sibling tabs");
            }
        } catch (error) {
            log.debug("Error in updateSiblingTabCounts:", error);
        }
      }
}
